import { Add, Warning } from '@mui/icons-material';
import {
    Box,
    Button,
    MenuItem,
    Select,
    SelectChangeEvent,
    TableCell,
    TextField,
    Tooltip,
    styled,
} from '@mui/material';
import { useState } from 'react';
import { DesignConceptPreview } from './DesignConceptPreview';

interface AddNewRankingRowProps {
    filteringOptionGroupOptions: [string, Models.IngestConfigApi.ForcedRankings.PartialProductSpec][];
    defaultOptionGroupOptions: [string, Models.IngestConfigApi.ForcedRankings.PartialProductSpec][];
    currentRankedConceptIds: string[];
    productKey: string;
    cultures: string[];
    onAdd: (ranking: Models.IngestConfigApi.ForcedRankings.Ranking) => void;
    onHide: () => void;
}

const StyledSelect = styled(Select<string>)(() => ({
    width: '100%',
    '& .MuiSelect-select': {
        padding: '8px 14px',
    },
}));

export const AddNewRankingRow = ({
    filteringOptionGroupOptions,
    defaultOptionGroupOptions,
    currentRankedConceptIds,
    productKey,
    cultures,
    onAdd,
    onHide,
}: AddNewRankingRowProps): JSX.Element => {
    const [rank, setRank] = useState<string>('1');
    const [conceptId, setConceptId] = useState<string>('');
    const [filteringOptionGroup, setFilteringOptionGroup] = useState<string>('');
    const [defaultOptionGroup, setDefaultOptionGroup] = useState<string>('');
    const [isConceptIdUnique, setIsConceptIdUnique] = useState<boolean>(true);

    const handleFilteringOptionChange = (event: SelectChangeEvent<string>): void => {
        setFilteringOptionGroup(event.target.value);
    };

    const handleDefaultOptionChange = (event: SelectChangeEvent<string>): void => {
        setDefaultOptionGroup(event.target.value);
    };
    const handleResetAndHide = (): void => {
        setRank('1');
        setConceptId('');
        setFilteringOptionGroup('');
        setDefaultOptionGroup('');
        setIsConceptIdUnique(true);
        onHide();
    };

    const handleAdd = (): void => {
        const rankNumber = parseInt(rank, 10);

        if (Number.isNaN(rankNumber) || !conceptId) return;

        onAdd({
            rank: rankNumber,
            designConceptId: conceptId,
            filteringProductSpecId: filteringOptionGroup || null,
            defaultProductSpecId: defaultOptionGroup || null,
        });
        handleResetAndHide();
    };

    const previewDefaultOptions = defaultOptionGroup
        ? defaultOptionGroupOptions.find(([id, _]) => id === defaultOptionGroup)?.[1].values
        : null;
    const previewFilteringOptions = filteringOptionGroup
        ? filteringOptionGroupOptions.find(([id, _]) => id === filteringOptionGroup)?.[1].values
        : null;

    return (
        <>
            <TableCell />
            <TableCell />
            <TableCell sx={{ paddingLeft: 0, paddingRight: 8 }}>
                <TextField
                    inputProps={{ min: 1 }}
                    size="small"
                    type="number"
                    value={rank}
                    onChange={(e): void => setRank(e.target.value)}
                />
            </TableCell>
            <TableCell sx={{ padding: 2 }}>
                <DesignConceptPreview
                    conceptId={conceptId}
                    cultures={cultures}
                    defaultOptions={previewDefaultOptions ?? {}}
                    filteringOptions={previewFilteringOptions ?? {}}
                    productKey={productKey}
                />
            </TableCell>
            <TableCell>
                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1, height: '100%',
                }}
                >
                    <TextField
                        color={!isConceptIdUnique ? 'warning' : 'primary'}
                        helperText={!isConceptIdUnique && 'Design concept already ranked'}
                        placeholder="Enter concept ID"
                        size="small"
                        value={conceptId}
                        onChange={(e): void => {
                            const newId = e.target.value;

                            setConceptId(newId);
                            setIsConceptIdUnique(!currentRankedConceptIds.includes(newId));
                        }}
                    />
                    {!isConceptIdUnique && (
                        <Tooltip title="Design concept already ranked. Ensure each duplicate design concept has a unique filtering option group.">
                            <Warning color="warning" />
                        </Tooltip>
                    )}
                </Box>
            </TableCell>
            <TableCell>
                <StyledSelect
                    displayEmpty
                    value={filteringOptionGroup}
                    onChange={handleFilteringOptionChange}
                >
                    <MenuItem value="">
                        <em>None</em>
                    </MenuItem>
                    {filteringOptionGroupOptions.map(([id, spec]) => (
                        <MenuItem key={id} value={id}>
                            {spec.name}
                        </MenuItem>
                    ))}
                </StyledSelect>
            </TableCell>
            <TableCell>
                <StyledSelect
                    displayEmpty
                    value={defaultOptionGroup}
                    onChange={handleDefaultOptionChange}
                >
                    <MenuItem value="">
                        <em>None</em>
                    </MenuItem>
                    {defaultOptionGroupOptions.map(([id, spec]) => (
                        <MenuItem key={id} value={id}>
                            {spec.name}
                        </MenuItem>
                    ))}
                </StyledSelect>
            </TableCell>
            <TableCell align="right">
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                        color="secondary"
                        size="small"
                        variant="text"
                        onClick={handleResetAndHide}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        disabled={!rank || !conceptId}
                        size="small"
                        startIcon={<Add />}
                        variant="outlined"
                        onClick={handleAdd}
                    >
                        Add
                    </Button>
                </Box>
            </TableCell>
        </>
    );
};
